'use client'

import { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { SearchIcon, MenuIcon } from '@/components/icons'
import Logo from './Logo'

/**
 * Custom hook to detect scroll position for header glass effects
 */
const useScrollPosition = () => {
  const [scrollPosition, setScrollPosition] = useState(0)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isDeepScrolled, setIsDeepScrolled] = useState(false)

  const handleScroll = useCallback(() => {
    const position = window.scrollY
    setScrollPosition(position)
    setIsScrolled(position > 50)
    setIsDeepScrolled(position > 200)
  }, [])

  useEffect(() => {
    // Throttle scroll events for better performance
    let ticking = false

    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', throttledHandleScroll, { passive: true })

    // Set initial position
    handleScroll()

    return () => window.removeEventListener('scroll', throttledHandleScroll)
  }, [handleScroll])

  return { scrollPosition, isScrolled, isDeepScrolled }
}

const navigationItems = [
  { name: 'Home', href: '/' },
  { name: 'Properties', href: '/properties' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' },
]

/**
 * Main navigation header with luxury design, dynamic logo, and scroll-responsive glass effects
 */
export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [logoData, setLogoData] = useState<{
    logoUrl?: string
    logoText?: string
  }>({})
  const pathname = usePathname()
  const { isScrolled, isDeepScrolled } = useScrollPosition()

  // Fetch logo data from content management
  useEffect(() => {
    const fetchLogoData = async () => {
      try {
        const response = await fetch('/api/content/logo')
        if (response.ok) {
          const data = await response.json()
          setLogoData({
            logoUrl: data.logoUrl,
            logoText: data.logoText,
          })
        } else {
          // Fallback to default
          setLogoData({ logoText: ' ' })
        }
      } catch (error) {
        console.error('Error fetching logo data:', error)
        setLogoData({ logoText: ' ' })
      }
    }

    fetchLogoData()
  }, [])

  const isActiveLink = (href: string) => {
    if (href === '/') {
      return pathname === '/'
    }
    return pathname.startsWith(href)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  // Dynamic header classes based on scroll position
  const getHeaderClasses = () => {
    const baseClasses =
      'navbar sticky top-0 z-50 transition-all duration-300 ease-out'

    if (isDeepScrolled) {
      return `${baseClasses} bg-base-200/75 backdrop-blur-lg border-b border-primary/15 shadow-2xl`
    } else if (isScrolled) {
      return `${baseClasses} bg-base-200/85 backdrop-blur-md border-b border-primary/10 shadow-luxury`
    } else {
      return `${baseClasses} bg-base-200/95 backdrop-blur-sm border-b border-accent/20 shadow-luxury`
    }
  }

  return (
    <header className={getHeaderClasses()}>
      <div className="container flex items-center justify-between mx-auto">
        {/* Left side - Mobile menu button and Desktop logo */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button - only on mobile */}
          <div className="lg:hidden">
            <div className="dropdown">
              <div
                tabIndex={0}
                role="button"
                className="btn btn-ghost text-base-content hover:text-primary"
                onClick={toggleMobileMenu}
                aria-label="Toggle mobile menu"
              >
                <MenuIcon className="w-5 h-5" />
              </div>
              {isMobileMenuOpen && (
                <ul
                  tabIndex={0}
                  className="menu dropdown-content menu-sm z-[1] mt-3 w-60 rounded-xl border border-primary/20 bg-base-200/90 p-4 shadow-2xl backdrop-blur-lg transition-all duration-300"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {navigationItems.map((item) => (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        className={`flex items-center gap-3 rounded-lg px-4 py-3 transition-all duration-200 ${
                          isActiveLink(item.href)
                            ? 'font-bold'
                            : 'font-medium text-base-content hover:font-bold hover:text-primary'
                        }`}
                      >
                        <span
                          className={`font-medium ${isActiveLink(item.href) ? 'font-bold' : ''}`}
                        >
                          {item.name}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Desktop Logo on left */}
          <div className="hidden lg:block">
            <Logo
              logoUrl={logoData.logoUrl}
              logoText={logoData.logoText}
              size="md"
            />
          </div>
        </div>

        {/* Center Logo - mobile only */}
        <div className="flex justify-center flex-1 lg:hidden">
          <Logo
            logoUrl={logoData.logoUrl}
            logoText={logoData.logoText}
            size="md"
          />
        </div>

        {/* Right side - Desktop navigation and Search */}
        <div className="flex items-center space-x-4">
          {/* Desktop navigation - right side */}
          <div className="hidden lg:flex">
            <ul className="space-x-2 menu menu-horizontal">
              {navigationItems.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2 transition-all duration-200 ${
                      isActiveLink(item.href)
                        ? 'font-bold'
                        : 'font-medium text-base-content hover:font-bold hover:text-primary'
                    }`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Search button */}
          <Link
            href="/properties"
            className="btn btn-circle btn-ghost text-base-content hover:text-primary"
            aria-label="Search Properties"
          >
            <SearchIcon className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </header>
  )
}
